import { useCallback, useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import moment from 'moment-timezone';

import { MARKET_DATA_DATE, MARKET_DATA_TIME_ZONE } from '../../lib/constants/date-time.constants';
import lang from '../../lib/language';
import { PlanPhaseTypeEnum, PurchasePaymentTypeEnum } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { getCurrentAccountTradingPlan, getCurrentDetailedAccount, normalisePlanPhaseKey } from '../../lib/store/crm/helpers';
import {
  formatCurrencyBySymbol,
  getCurrencySymbolByCurrencyCode,
} from '../../lib/util/DataHelpers';

import LimitReachedIcon from '../../assets/img/DailyLossReached.png';
import WarningIcon from '../../assets/img/DailyLossWarning.png';
import { GTM_CATEGORIES, GTM_EVENTS, GTM_FEATURE_USAGE_REWIND } from '../../constants/gtm';
import { PaymentMethodSelectionState } from '../../containers/Enrollment/PaymentMethodSelection/types';
import { useIsMobile } from '../../hooks';
import BaseModal from '../../shared_components/BaseModal/BaseModal';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { hashSHA256 } from '../../util/hash';
import {
  getModalShownStatus,
  RESET_DAILY_LOSS_CONFIRM_MODAL_KEY,
  setModalShownStatus,
} from '../../util/localStorageHelpers';
import { UI_ROUTES } from '../../util/routes';
import { MobileDrawer } from '../index';

import styles from './ResetDailyLossConfirmModal.module.scss';

type Props = {
  toggleModal: () => void;
  isOpen: boolean;
  isDailyLossReached: boolean;
  warningLevel?: 50 | 75 | null;
  isComingFromRewindButtonClick?: boolean;
} & ReduxProps;

const ResetDailyLossConfirmModal = (props: Props) => {
  const {
    individualId,
    accountDetail,
    isOpen,
    toggleModal,
    isComingFromRewindButtonClick = false,
    isDailyLossReached,
    currentTradingPlan,
    warningLevel,
    accountStatusData,
    userId,
    userEmail,
  } = props;
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isResetBtnClicked, setIsResetBtnClicked] = useState<boolean>(false);
  const hasTrackedRewind = useRef<boolean>(false);
  const [modalContent, setModalContent] = useState<{
    title?: string;
    description?: string;
    buttonText?: string;
  }>({});
  const isChallengePhase = currentTradingPlan?.phase === PlanPhaseTypeEnum.challenge;

  const { rewind_daily_loss_price, rewind_daily_loss_price_ccy, phase } = currentTradingPlan ?? {};
  const { dailyResetCount } = accountStatusData;

  const getUserPreferencesKey = useCallback(() => {
    if (!phase || !individualId || !accountDetail?.account_id) return '';
    const normalisePhase = normalisePlanPhaseKey(phase);
    const todayInNY = moment.tz(MARKET_DATA_TIME_ZONE).format(MARKET_DATA_DATE);
    return `${individualId}:${accountDetail.account_id}:${normalisePhase}:${dailyResetCount}:${warningLevel}:${todayInNY}`;
  }, [individualId, accountDetail?.account_id, phase, dailyResetCount, warningLevel]);

  useEffect(() => {
    if (phase && isOpen) {
      const subKey = getUserPreferencesKey();
      if (!subKey) {
        return;
      }
      const localStorageData = getModalShownStatus(
        RESET_DAILY_LOSS_CONFIRM_MODAL_KEY,
        'ResetDailyLossConfirmModal',
      );
      const isModalShowed = localStorageData[subKey] === true;
      setShowModal(!isModalShowed);
    } else {
      setShowModal(false);
    }
  }, [getUserPreferencesKey, phase, isOpen]);

  const trackRewindUsage = async () => {
    if (hasTrackedRewind.current) return;
    hasTrackedRewind.current = true;
    const hashedEmail = await hashSHA256(userEmail || '');
    TagManager.dataLayer({
      dataLayer: {
        event: GTM_EVENTS.FEATURE_USAGE,
        event_category: GTM_CATEGORIES.PLATFORM_INTERACTION,
        event_action: GTM_FEATURE_USAGE_REWIND.ACTION,
        event_label: GTM_FEATURE_USAGE_REWIND.LABEL,
        client_id: userId,
        user_email_hashed: hashedEmail,
        feature_name: GTM_FEATURE_USAGE_REWIND.FEATURE_NAME,
        challenge_id: currentTradingPlan?.reference_id,
      },
    });
  };

  const handleCreatePurchasePayment = () => {
    sessionStorage.removeItem('paymentGatewayURL');
    if (!individualId || !accountDetail?.account_id || isResetBtnClicked) {
      return;
    }

    trackRewindUsage();
    setIsResetBtnClicked(true);

    const historyStateParams: PaymentMethodSelectionState = {
      accountId: accountDetail.account_id,
      individualId,
      purchaseType: PurchasePaymentTypeEnum.AccountDailyLossRewind,
      prevPage: UI_ROUTES.products,
    };

    toggleModal();

    navigate(
      {
        pathname: UI_ROUTES.paymentMethodSelection,
      },
      { state: historyStateParams },
    );
  };

  // Determine modal content based on warning level
  const getModalContent = useCallback(() => {
    const resetDisplayPrice = formatCurrencyBySymbol(
      rewind_daily_loss_price,
      getCurrencySymbolByCurrencyCode(rewind_daily_loss_price_ccy),
    );

    if (warningLevel === 50) {
      return {
        title: lang.dailyLossLimit2(50),
        description: isChallengePhase
          ? lang.dailyLossLimit50PercentDescription()
          : lang.dailyLossLimit50PercentNonChallengeDescription(),
        buttonText: lang.tradingPlanResetDailyLossBtn(),
      };
    }
    if (warningLevel === 75) {
      return {
        title: lang.dailyLossLimit2(75),
        description: isChallengePhase
          ? lang.dailyLossLimit75PercentDescription()
          : lang.dailyLossLimit75PercentNonChallengeDescription(),
        buttonText: lang.tradingPlanResetDailyLossBtn(),
      };
    }
    // Original behavior for 100% or manual reset
    if (isChallengePhase) {
      return {
        title: isDailyLossReached
          ? lang.tradingPlanDailyLossReachedTitle()
          : lang.tradingPlanResetDailyLossBtn(),
        description: isDailyLossReached
          ? lang.tradingPlanDailyLossReachedDesc()
          : lang.tradingPlanResetDailyLossDesc(),
        buttonText: lang.tradingPlanResetDailyLossConfirmBtn(resetDisplayPrice),
      };
    }
    return {
      title: lang.tradingPlanDailyLossReachedTitle(),
      description: lang.tradingPlanDailyLossReachedNonChallengeDesc(),
      buttonText: '',
    };
  }, [
    rewind_daily_loss_price,
    rewind_daily_loss_price_ccy,
    warningLevel,
    isChallengePhase,
    isDailyLossReached,
  ]);

  const renderCloseButtonText = () => {
    if (isChallengePhase) {
      return isDailyLossReached ? lang.commonNotNow() : lang.commonCancelButton();
    }
    return lang.commonOK();
  };
  const htmlContent: JSX.Element = (
    <>
      <div className={styles.iconHolder}>
        <img
          src={warningLevel ? WarningIcon : LimitReachedIcon}
          alt="Warning icon"
          className={styles.warningIcon}
        />
      </div>
      <div className={styles.textWrapper}>
        <div className={styles.title}>{modalContent?.title}</div>
        <div className={styles.text}>{modalContent?.description}</div>
      </div>
      <div className={styles.buttonsWrapper}>
        {isChallengePhase && modalContent?.buttonText && (
          <Button
            fullWidth
            biggerFontSize
            disabled={isResetBtnClicked}
            variant={ButtonTypesEnum.Primary}
            onClick={() => {
              if (!isComingFromRewindButtonClick) {
                const subKey = getUserPreferencesKey();
                if (subKey) {
                  setModalShownStatus(
                    RESET_DAILY_LOSS_CONFIRM_MODAL_KEY,
                    subKey,
                    true,
                    'ResetDailyLossConfirmModal',
                  );
                }
              }
              handleCreatePurchasePayment();
            }}
            className={styles.closeButton}
          >
            {modalContent?.buttonText}
          </Button>
        )}
        <Button
          fullWidth
          biggerFontSize
          variant={isChallengePhase ? ButtonTypesEnum.Secondary : ButtonTypesEnum.Primary}
          onClick={() => {
            if (!isComingFromRewindButtonClick) {
              const subKey = getUserPreferencesKey();
              if (subKey) {
                setModalShownStatus(
                  RESET_DAILY_LOSS_CONFIRM_MODAL_KEY,
                  subKey,
                  true,
                  'ResetDailyLossConfirmModal',
                );
              }
            }
            setIsResetBtnClicked(false);
            toggleModal();
          }}
          className={styles.closeButton}
        >
          {renderCloseButtonText()}
        </Button>
      </div>
    </>
  );

  useEffect(() => {
    setModalContent(getModalContent());
  }, [getModalContent]);

  if (!phase) {
    return null;
  }

  return (
    <>
      {isMobile ? (
        <MobileDrawer
          isOpen={isOpen && (showModal || isComingFromRewindButtonClick)}
          title={''}
          onClose={() => {
            if (!isComingFromRewindButtonClick) {
              const subKey = getUserPreferencesKey();
              if (subKey) {
                setModalShownStatus(
                  RESET_DAILY_LOSS_CONFIRM_MODAL_KEY,
                  subKey,
                  true,
                  'ResetDailyLossConfirmModal',
                );
              }
            }
            toggleModal();
          }}
        >
          {htmlContent}
        </MobileDrawer>
      ) : (
        <BaseModal
          isOpen={isOpen && (showModal || isComingFromRewindButtonClick)}
          toggle={() => {
            if (!isComingFromRewindButtonClick) {
              const subKey = getUserPreferencesKey();
              if (subKey) {
                setModalShownStatus(
                  RESET_DAILY_LOSS_CONFIRM_MODAL_KEY,
                  subKey,
                  true,
                  'ResetDailyLossConfirmModal',
                );
              }
            }
            toggleModal();
          }}
        >
          {htmlContent}
        </BaseModal>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountStatusData: state.closeout.accountStatusData,
  accountDetail: getCurrentDetailedAccount(state),
  individualId: state.crm.individualOrCompanyEmployeeId,
  currentTradingPlan: getCurrentAccountTradingPlan(state.crm),
  userId: state.auth.enroll.userId,
  userEmail: state.crm.individualExtendedInfo?.email,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ResetDailyLossConfirmModal);
